import React, { useEffect, useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { getProjects } from '../data/projects';
import { categories } from '../data/products';
import { ArrowRight, CheckCircle2, ChevronRight, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { JsonLd, useSEO } from '../utils/seo';
export function Project() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const projects = getProjects();
  const project = projects.find(p => p.slug === slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  // Mobile-only ordering: active project pinned to position 0 so it's
  // always the first thing the user sees in the horizontal scroller —
  // no need to scroll to find "you are here". The sm+ grid keeps the
  // canonical order for desktop browsing.
  const activeIndex = project ? projects.findIndex(p => p.slug === project.slug) : -1;
  const mobileProjects = activeIndex >= 0 ? [...projects.slice(activeIndex), ...projects.slice(0, activeIndex)] : projects;
  // Reset the mobile scroller back to the start whenever the active
  // project changes so the freshly-promoted "first" pill is actually
  // visible — otherwise the previous scrollLeft value persists and the
  // user sees the pills already partially scrolled.
  const mobileScrollerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (mobileScrollerRef.current) {
      mobileScrollerRef.current.scrollLeft = 0;
    }
  }, [slug]);
  const projectDescription = project ? `${project.tagline} See recommended materials, depths, and practical tips for a ${project.name.toLowerCase()} project in the Puget Sound area.` : 'Explore practical gravel, drainage, patio, and landscaping project guides from 206Gravel.';
  useSEO({
    title: project ? `${project.name} Materials & Pro Tips | 206Gravel` : 'Gravel Project Guides | 206Gravel',
    description: projectDescription,
    path: project ? `/projects/${project.slug}` : '/'
  });
  if (!project) {
    return <Navigate to="/" replace data-id="element-3090" data-mp="3bq05pgrid5ls" />;
  }
  const Icon = project.icon;
  // Helper to find full product details from data/products.ts
  const getProductDetails = (productSlug: string) => {
    for (const cat of categories) {
      const product = cat.products.find(p => p.slug === productSlug);
      if (product) {
        return {
          ...product,
          categoryName: cat.name
        };
      }
    }
    return null;
  };
  return <main className="min-h-screen bg-[#fdfbf7] dark:bg-[#36322F] pt-8 pb-24" data-id="element-3091" data-mp="3vf0vf767lje7">
      <JsonLd data={[{
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: project.name,
      description: projectDescription,
      url: `https://206gravel.com/projects/${project.slug}`,
      supply: project.materials.map(material => ({
        '@type': 'HowToSupply',
        name: material.role
      })),
      step: project.tips.map((tip, index) => ({
        '@type': 'HowToStep',
        position: index + 1,
        text: tip
      }))
    }, {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://206gravel.com/'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: project.name,
        item: `https://206gravel.com/projects/${project.slug}`
      }]
    }]} data-id="element-3092" data-mp="372gbr8roa7q" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3093" data-mp="2qe1bhmf7m817">
        {/* Breadcrumbs */}
        <nav className="flex items-center text-sm text-stone-500 dark:text-stone-400 mb-6" aria-label="Breadcrumb" data-id="element-3094" data-mp="2le59fsq2oqoa">
          <Link to="/" className="hover:text-brand-orange transition-colors" data-id="element-3095" data-mp="2gek9bez62c9z">
            Home
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" data-id="element-3096" data-mp="2bfpf7gvr11ak" />
          <span className="text-stone-900 dark:text-white font-medium" data-id="element-3097" data-mp="150exy0wmj04m">
            {project.name}
          </span>
        </nav>

        {/* Project switcher — bold card-style tab grid. Each tab shows
            the project icon prominently with the project name as the
            primary label and typical yards as a quiet subtitle. Active
            project gets a saturated sage fill so the current context is
            unmissable; inactive tabs have a clear hover state to invite
            switching. 2 columns on mobile, 3 on sm+, 4 on md+. */}
        <div className="mb-10" data-id="element-3098" data-mp="2jwy96muj35tp">
          <h2 className="text-xs font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400 mb-3" data-id="element-3099" data-mp="37m0wobbufsw4">
            Shop by Project
          </h2>
          {/* Mobile: horizontal scrollable pill row so the project content
           stays visible below the tabs (no long vertical menu to scroll past).
           sm+: switches to a multi-column grid for desktop browsing. */}
          <div ref={mobileScrollerRef} className="flex sm:hidden gap-2 overflow-x-auto pb-2 snap-x snap-mandatory" style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }} data-id="element-3100" data-mp="32p0q7zb30o4h">
            <style data-id="element-3101" data-mp="1r5ehsfns2lgm">{`
              .project-tabs-mobile::-webkit-scrollbar { display: none; }
            `}</style>
            {mobileProjects.map(p => {
            const PIcon = p.icon;
            const isActive = p.slug === project.slug;
            return <Link key={p.slug} to={`/projects/${p.slug}`} className={`group shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-all snap-start ${isActive ? 'bg-[#04A05C] border-[#04A05C] shadow-md shadow-[#04A05C]/20' : 'bg-white dark:bg-stone-900/60 border-stone-200 dark:border-white/10'}`} aria-current={isActive ? 'page' : undefined} data-id="element-3102" data-mp="i9sujw6jzrv1">
                  <div className={`w-7 h-7 shrink-0 rounded-lg flex items-center justify-center ${isActive ? 'bg-white/20' : 'bg-[#e8ede4] dark:bg-emerald-500/15'}`} data-id="element-3103" data-mp="3vh41441ddjia">
                    <PIcon className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-[#3d7530] dark:text-[#9dd194]'}`} data-id="element-3104" data-mp="3qoj9n95xrihx" />
                  </div>
                  <div className="flex flex-col" data-id="element-3105" data-mp="2z09doysnp5pj">
                    <span className={`font-bold text-xs leading-tight whitespace-nowrap ${isActive ? 'text-white' : 'text-stone-900 dark:text-white'}`} data-id="element-3106" data-mp="2yxdg5bkq0j1m">
                      {p.name}
                    </span>
                    <span className={`text-[10px] font-medium ${isActive ? 'text-white/80' : 'text-stone-500 dark:text-stone-400'}`} data-id="element-3107" data-mp="16vk7bhi2hnqa">
                      {p.typicalYards}
                    </span>
                  </div>
                </Link>;
          })}
          </div>

          {/* sm+ grid */}
          <div className="hidden sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" data-id="element-3108" data-mp="j2c4kr69tifh">
            {projects.map(p => {
            const PIcon = p.icon;
            const isActive = p.slug === project.slug;
            return <Link key={p.slug} to={`/projects/${p.slug}`} className={`group relative flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-all ${isActive ? 'bg-[#04A05C] dark:bg-[#04A05C] border-[#04A05C] dark:border-[#04A05C] shadow-md shadow-[#04A05C]/20' : 'bg-white dark:bg-stone-900/60 border-stone-200 dark:border-white/10 hover:border-[#04A05C] dark:hover:border-[#22c95c] hover:shadow-sm'}`} aria-current={isActive ? 'page' : undefined} data-id="element-3109" data-mp="xwynzk4ru4og">
                  <div className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center transition-colors ${isActive ? 'bg-white/20' : 'bg-[#e8ede4] dark:bg-emerald-500/15 group-hover:bg-[#d4ddcf] dark:group-hover:bg-emerald-500/25'}`} data-id="element-3110" data-mp="3oxfx9c2s24zb">
                    <PIcon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-[#3d7530] dark:text-[#9dd194]'}`} data-id="element-3111" data-mp="3a6p5u553l7wq" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-3112" data-mp="363k3q3oxj88m">
                    <div className={`font-bold text-sm leading-tight ${isActive ? 'text-white' : 'text-stone-900 dark:text-white'}`} data-id="element-3113" data-mp="3158vtyi737t1">
                      {p.name}
                    </div>
                    <div className={`text-[10px] mt-0.5 font-medium ${isActive ? 'text-white/80' : 'text-stone-500 dark:text-stone-400'}`} data-id="element-3114" data-mp="ze5hoz93jb3q">
                      {p.typicalYards}
                    </div>
                  </div>
                </Link>;
          })}
          </div>
        </div>

        {/* Header */}
        <div className="mb-12" data-id="element-3115" data-mp="89b97vicssg6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-orange/10 text-brand-orange mb-6" data-id="element-3116" data-mp="88iclnd3zjbr">
            <Icon className="w-8 h-8" data-id="element-3117" data-mp="3pnp08o83d7ad" />
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-black text-stone-900 dark:text-white mb-4" data-id="element-3118" data-mp="3unbf123kivla">
            {project.name}
          </h1>
          <p className="text-xl md:text-2xl text-stone-600 dark:text-stone-300 font-medium mb-6" data-id="element-3119" data-mp="3lny6cbn9qb1m">
            {project.tagline}
          </p>
          <div className="prose prose-lg dark:prose-invert text-stone-600 dark:text-stone-400 max-w-none" data-id="element-3120" data-mp="36rqj12gkcti4">
            <p data-id="element-3121" data-mp="3bqjpwc1chm9b">{project.intro}</p>
          </div>
          <div className="mt-6 inline-flex items-center px-4 py-2 rounded-full bg-stone-100 dark:bg-white/5 text-stone-700 dark:text-stone-300 text-sm font-medium border border-stone-200 dark:border-white/10" data-id="element-3122" data-mp="14zar4gke93bf">
            <span className="mr-2" data-id="element-3123" data-mp="2ug6a47h8bb11">Typical project size:</span>
            <span className="font-bold text-stone-900 dark:text-white" data-id="element-3124" data-mp="2ued4mvicqwum">
              {project.typicalYards}
            </span>
          </div>
        </div>

        {/* Materials Section */}
        <div className="mb-16" data-id="element-3125" data-mp="347k8gbxg24sl">
          <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6" data-id="element-3126" data-mp="2zfivsowd6c21">
            Recommended Materials
          </h2>
          <div className="space-y-6" data-id="element-3127" data-mp="3d5mcz1gdzfkm">
            {project.materials.map((mat, idx) => {
            const product = getProductDetails(mat.slug);
            if (!product) return null;
            return <motion.div key={mat.slug} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.1
            }} className="bg-[#ffffff] dark:bg-[#36322F] rounded-2xl p-6 shadow-sm border border-stone-200 dark:border-white/10 flex flex-col md:flex-row gap-6 items-start" data-id="element-3128" data-mp="3n423p3bi4mtl" data-mpi={idx}>
                  <div className="w-full md:w-48 h-32 shrink-0 rounded-xl overflow-hidden bg-stone-100 dark:bg-stone-800" data-id="element-3129" data-mp="2w3ozcetp5s3">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-3130" data-mp="3u3omjtb1f6ny" />
                  </div>
                  <div className="flex-1" data-id="element-3131" data-mp="3l0xb3ti5wawb">
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-2" data-id="element-3132" data-mp="3l28lwda0yiwx">
                      <div data-id="element-3133" data-mp="lkqws2odw99d">
                        <div className="flex items-center gap-2 mb-1" data-id="element-3134" data-mp="ll0obup2uoh6">
                          <span className="text-xs font-bold uppercase tracking-wider text-brand-orange" data-id="element-3135" data-mp="go0ed8p3x1ks">
                            {mat.role}
                          </span>
                          <span className="text-stone-300 dark:text-stone-600" data-id="element-3136" data-mp="7v6wr4m6tq3r">
                            •
                          </span>
                          <span className="text-xs font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400" data-id="element-3137" data-mp="14dl0g0gk4eya">
                            Depth: {mat.depth}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-stone-900 dark:text-white" data-id="element-3138" data-mp="2n7efxyxitd16">
                          {product.name}
                        </h3>
                      </div>
                      <div className="text-right" data-id="element-3139" data-mp="2rbcby0rju7q0">
                        <div className="text-lg font-bold text-stone-900 dark:text-white" data-id="element-3140" data-mp="2w95ln3s29jv1">
                          ${product.pricePerYard}
                          <span className="text-sm font-normal text-stone-500 dark:text-stone-400" data-id="element-3141" data-mp="315cqet8q0pbe">
                            /yd
                          </span>
                        </div>
                      </div>
                    </div>

                    <p className="text-stone-600 dark:text-stone-300 mb-4" data-id="element-3142" data-mp="3eyu1dpncctpt">
                      <span className="font-semibold text-stone-900 dark:text-white" data-id="element-3143" data-mp="3jxx4v5noarvo">
                        Why:{' '}
                      </span>
                      {mat.why}
                    </p>

                    <Link to={`/shop/${product.slug}`} className="inline-flex items-center text-sm font-bold text-brand-orange hover:text-brand-orange/80 transition-colors" data-id="element-3144" data-mp="1otz2dm3k8nrs">
                      View product details
                      <ArrowRight className="w-4 h-4 ml-1" data-id="element-3145" data-mp="1yld8wdtxinuf" />
                    </Link>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12" data-id="element-3146" data-mp="27kgt5l4u19ai">
          {/* Pro Tips */}
          <div data-id="element-3147" data-mp="23jet5233ct7h">
            <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6 flex items-center" data-id="element-3148" data-mp="23jxyzkfgvr55">
              <CheckCircle2 className="w-6 h-6 mr-2 text-[#04A05C]" data-id="element-3149" data-mp="2hjfpc48evcgm" />
              Pro Tips
            </h2>
            <ul className="space-y-4" data-id="element-3150" data-mp="2cl4fhti491ye">
              {project.tips.map((tip, idx) => <li key={idx} className="flex items-start" data-id="element-3151" data-mp="n4mxmganf5ty" data-mpi={idx}>
                  <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-brand-orange mt-2 mr-3" data-id="element-3152" data-mp="n7956fxjhlc6" />
                  <span className="text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-3153" data-mp="i86ddx6klbm5">
                    {tip}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* FAQs */}
          <div data-id="element-3154" data-mp="2k3lweksbnj1e">
            <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6 flex items-center" data-id="element-3155" data-mp="2k45eq2lvw9fy">
              <HelpCircle className="w-6 h-6 mr-2 text-brand-orange" data-id="element-3156" data-mp="2p1x52s6vrni1" />
              Project FAQs
            </h2>
            <div className="space-y-6" data-id="element-3157" data-mp="2b24jfba7nxgx">
              {project.faqs.map((faq, idx) => <div key={idx} data-id="element-3158" data-mp="2g2jwe1qkz2yd" data-mpi={idx}>
                  <h4 className="font-bold text-stone-900 dark:text-white mb-2" data-id="element-3159" data-mp="2g3lid194x9uv">
                    {faq.q}
                  </h4>
                  <p className="text-stone-600 dark:text-stone-300 text-sm leading-relaxed" data-id="element-3160" data-mp="21746m8x19t7s">
                    {faq.a}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-2xl p-8 md:p-12 text-center bg-[#57534e]" data-id="element-3161" data-mp="4czmztbig5h7">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4" data-id="element-3162" data-mp="20fm6vg6icl40">
            Ready to start your project?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto" data-id="element-3163" data-mp="1wfc4edhxcctw">
            Get an instant estimate including delivery for all the materials you
            need.
          </p>
          <Link to={`/?from=project&project=${project.slug}#estimator`} className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-brand-orange hover:bg-brand-orange/90 rounded-xl transition-colors" data-id="element-3164" data-mp="1mgcukrlfab5b">
            Get an Estimate
            <ArrowRight className="w-5 h-5 ml-2" data-id="element-3165" data-mp="1co73dk2zvxr6" />
          </Link>
        </div>
      </div>
    </main>;
}